import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import salesRadarService from '../../services/salesRadarService';

// Stale threshold in days
const STALE_THRESHOLD_DAYS = 14;

const getScoreColor = (score) => {
  if (score >= 70) return '#16A34A';
  if (score >= 50) return '#F59E0B';
  return '#DC2626';
};

const getPriorityStyle = (priority) => {
  switch (priority) {
    case 'high': return { bg: '#FEE2E2', text: '#DC2626' };
    case 'medium': return { bg: '#FEF3C7', text: '#D97706' };
    default: return { bg: 'var(--gray-100)', text: 'var(--gray-600)' };
  }
};

const formatCurrency = (value) => {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(value);
};

// Lead Card Component
const LeadCard = ({ lead, isStale, onMenuOpen, onQuickAction, isSelected, onSelect }) => {
  const cardRef = useRef(null);
  const navigate = useNavigate();

  const handleDragStart = (e) => {
    e.dataTransfer.setData('leadId', lead.id.toString());
    e.dataTransfer.setData('sourceStage', lead.current_stage);
    cardRef.current?.classList.add('dragging');
  };

  const handleDragEnd = () => {
    cardRef.current?.classList.remove('dragging');
  };

  const priorityStyle = getPriorityStyle(lead.priority);

  return (
    <div
      ref={cardRef}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{
        marginBottom: '0.75rem',
        cursor: 'grab',
        transition: 'all 0.2s',
        border: isStale ? '2px solid #F59E0B' : isSelected ? '2px solid #2563EB' : '1px solid var(--gray-200)',
        backgroundColor: isSelected ? '#EFF6FF' : 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ padding: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(lead.id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ color: 'var(--gray-400)', cursor: 'grab' }}>⋮⋮</span>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: getScoreColor(lead.score),
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: '600',
            }}>
              {lead.score}
            </div>
          </div>
          {isStale && (
            <span title={`No activity for ${lead.days_since_activity} days`} style={{ color: '#F59E0B' }}>
              ⚠️
            </span>
          )}
          <button
            onClick={(e) => onMenuOpen(e, lead)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              color: 'var(--gray-500)',
            }}
          >
            ⋯
          </button>
        </div>

        <div style={{ marginTop: '0.5rem' }}>
          <div 
            style={{ fontWeight: '500', color: 'var(--gray-900)', cursor: 'pointer' }}
            onClick={() => navigate(`/sales-radar/leads/${lead.id}`)}
          >
            {lead.first_name} {lead.last_name}
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            🏢 {lead.company_name || 'Unknown Company'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          {lead.estimated_value && (
            <span style={{
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              border: '1px solid var(--gray-300)',
              background: 'white',
            }}>
              💰 {formatCurrency(lead.estimated_value)}
            </span>
          )}
          {lead.priority && (
            <span style={{
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              background: priorityStyle.bg,
              color: priorityStyle.text,
            }}>
              {lead.priority}
            </span>
          )}
        </div>

        {lead.tags && lead.tags.length > 0 && (
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            {lead.tags.slice(0, 2).map(tag => (
              <span key={tag} style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                fontSize: '0.625rem',
                border: '1px solid var(--gray-200)',
                background: 'var(--gray-50)',
                color: 'var(--gray-600)',
              }}>
                {tag}
              </span>
            ))}
            {lead.tags.length > 2 && (
              <span style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                fontSize: '0.625rem',
                background: 'var(--gray-100)',
                color: 'var(--gray-600)',
              }}>
                +{lead.tags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{
        padding: '0.5rem 0.75rem',
        borderTop: '1px solid var(--gray-100)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            title="Send Email"
            onClick={() => onQuickAction('email', lead)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
          >
            ✉️
          </button>
          <button
            title="Call"
            onClick={() => onQuickAction('call', lead)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
          >
            📞
          </button>
          <button
            title="Schedule"
            onClick={() => onQuickAction('schedule', lead)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
          >
            📅
          </button>
        </div>
        <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
          {lead.days_since_activity}d ago
        </span>
      </div>
    </div>
  );
};

// Kanban Column Component
const KanbanColumn = ({
  stage,
  leads,
  onDrop,
  onMenuOpen,
  onQuickAction,
  selectedLeads,
  onSelectLead
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const leadId = parseInt(e.dataTransfer.getData('leadId'));
    const sourceStage = e.dataTransfer.getData('sourceStage');
    if (sourceStage !== stage.id) {
      onDrop(leadId, stage.id, sourceStage);
    }
  };

  const stageLeads = leads.filter(l => l.current_stage === stage.id);
  const totalValue = stageLeads.reduce((sum, l) => sum + (l.estimated_value || 0), 0);
  const staleCount = stageLeads.filter(l => l.days_since_activity >= STALE_THRESHOLD_DAYS).length;

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        width: '300px',
        minWidth: '300px',
        backgroundColor: isDragOver ? '#EFF6FF' : 'var(--gray-100)',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
        transition: 'background-color 0.2s',
        border: isDragOver ? '2px dashed #2563EB' : '2px solid transparent',
      }}
    >
      {/* Column Header */}
      <div style={{
        padding: '1rem',
        borderBottom: '1px solid var(--gray-200)',
        backgroundColor: stage.color || '#6B7280',
        borderRadius: '8px 8px 0 0',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: '600' }}>{stage.name}</span>
          <span style={{
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            background: 'rgba(255,255,255,0.3)',
          }}>
            {stageLeads.length}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.75rem' }}>
          <span>{formatCurrency(totalValue)}</span>
          {staleCount > 0 && (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              background: 'rgba(251,191,36,0.3)',
              color: '#FEF3C7',
            }}>
              ⚠️ {staleCount}
            </span>
          )}
        </div>
      </div>

      {/* Column Content */}
      <div style={{
        padding: '0.75rem',
        flex: 1,
        overflowY: 'auto',
        minHeight: '200px',
      }}>
        {stageLeads.length === 0 ? (
          <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--gray-400)',
            fontSize: '0.875rem',
          }}>
            Drop leads here
          </div>
        ) : (
          stageLeads.map(lead => (
            <LeadCard
              key={lead.id}
              lead={lead}
              isStale={lead.days_since_activity >= STALE_THRESHOLD_DAYS}
              onMenuOpen={onMenuOpen}
              onQuickAction={onQuickAction}
              isSelected={selectedLeads.includes(lead.id)}
              onSelect={onSelectLead}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Main Kanban Board Component
const PipelineKanban = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    scoreRange: [0, 100],
    showStaleOnly: false,
    priority: ''
  });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [menuOpen, setMenuOpen] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [bulkMoveDialog, setBulkMoveDialog] = useState(false);
  const [filterDialog, setFilterDialog] = useState(false);
  const [movingLead, setMovingLead] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use mock data if API fails
      try {
        const [leadsRes, stagesRes] = await Promise.all([
          salesRadarService.getLeads({ project_id: projectId, limit: 200 }),
          salesRadarService.getStages(projectId)
        ]);

        if (leadsRes.success) {
          setLeads(leadsRes.data || []);
        }
        if (stagesRes.success) {
          setStages(stagesRes.data || []);
        }
      } catch {
        // Use mock data
        setStages([
          { id: 'new', name: 'New Leads', color: '#6B7280' },
          { id: 'contacted', name: 'Contacted', color: '#2563EB' },
          { id: 'qualified', name: 'Qualified', color: '#7C3AED' },
          { id: 'proposal', name: 'Proposal', color: '#F59E0B' },
          { id: 'negotiation', name: 'Negotiation', color: '#DC2626' },
          { id: 'won', name: 'Won', color: '#16A34A' },
        ]);
        setLeads([
          { id: 1, first_name: 'John', last_name: 'Smith', company_name: 'Acme Corp', email: 'john@acme.com', score: 85, current_stage: 'new', estimated_value: 50000, priority: 'high', days_since_activity: 2, tags: ['enterprise', 'demo requested'] },
          { id: 2, first_name: 'Sarah', last_name: 'Johnson', company_name: 'TechStart', email: 'sarah@techstart.io', score: 72, current_stage: 'contacted', estimated_value: 25000, priority: 'medium', days_since_activity: 5, tags: ['startup'] },
          { id: 3, first_name: 'Michael', last_name: 'Chen', company_name: 'DataFlow', email: 'mike@dataflow.com', score: 91, current_stage: 'qualified', estimated_value: 75000, priority: 'high', days_since_activity: 1, tags: ['enterprise', 'referral'] },
          { id: 4, first_name: 'Emma', last_name: 'Wilson', company_name: 'CloudBase', email: 'emma@cloudbase.io', score: 45, current_stage: 'new', estimated_value: 15000, priority: 'low', days_since_activity: 18, tags: [] },
          { id: 5, first_name: 'David', last_name: 'Brown', company_name: 'InnovateTech', email: 'david@innovate.tech', score: 68, current_stage: 'proposal', estimated_value: 35000, priority: 'medium', days_since_activity: 3, tags: ['trial user'] },
        ]);
      }
    } catch (err) {
      setError('Failed to load kanban data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStageDrop = async (leadId, newStage, oldStage) => {
    if (newStage === oldStage) return;

    setMovingLead(true);

    // Optimistically update UI
    setLeads(prev => prev.map(lead =>
      lead.id === leadId ? { ...lead, current_stage: newStage } : lead
    ));

    try {
      await salesRadarService.updateLeadStage(leadId, newStage);
    } catch (err) {
      console.error('Failed to update stage:', err);
      // Revert on error
      setLeads(prev => prev.map(lead =>
        lead.id === leadId ? { ...lead, current_stage: oldStage } : lead
      ));
    } finally {
      setMovingLead(false);
    }
  };

  const handleBulkMove = async (targetStage) => {
    if (selectedLeads.length === 0) return;

    setMovingLead(true);

    try {
      await Promise.all(
        selectedLeads.map(leadId => salesRadarService.updateLeadStage(leadId, targetStage))
      );

      setLeads(prev => prev.map(lead =>
        selectedLeads.includes(lead.id) ? { ...lead, current_stage: targetStage } : lead
      ));

      setSelectedLeads([]);
      setBulkMoveDialog(false);
    } catch (err) {
      console.error('Failed to bulk move leads:', err);
    } finally {
      setMovingLead(false);
    }
  };

  const handleMenuOpen = (event, lead) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuOpen({ x: rect.left, y: rect.bottom });
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setMenuOpen(null);
    setSelectedLead(null);
  };

  const handleQuickAction = (action, lead) => {
    switch (action) {
      case 'email':
        window.location.href = `mailto:${lead.email}`;
        break;
      case 'call':
        window.location.href = `tel:${lead.phone}`;
        break;
      case 'schedule':
        break;
      default:
        break;
    }
  };

  const handleSelectLead = (leadId, selected) => {
    setSelectedLeads(prev =>
      selected ? [...prev, leadId] : prev.filter(id => id !== leadId)
    );
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        lead.first_name?.toLowerCase().includes(query) ||
        lead.last_name?.toLowerCase().includes(query) ||
        lead.email?.toLowerCase().includes(query) ||
        lead.company_name?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    if (lead.score < filters.scoreRange[0] || lead.score > filters.scoreRange[1]) {
      return false;
    }

    if (filters.showStaleOnly && lead.days_since_activity < STALE_THRESHOLD_DAYS) {
      return false;
    }

    if (filters.priority && lead.priority !== filters.priority) {
      return false;
    }

    return true;
  });

  if (loading && leads.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div style={{ color: 'var(--gray-500)' }}>Loading pipeline...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav style={{
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        color: 'var(--gray-600)',
        fontSize: '0.875rem'
      }}>
        <button onClick={() => navigate('/')} className="btn btn-link" style={{ padding: 0, color: 'var(--primary-600)' }}>
          Home
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <button onClick={() => navigate('/sales-radar')} className="btn btn-link" style={{ padding: 0, color: 'var(--primary-600)' }}>
          Sales Radar
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <span style={{ color: 'var(--gray-600)' }}>Pipeline</span>
      </nav>

      {/* Header Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '3px solid #7C3AED'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 Pipeline Kanban
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Drag and drop leads between stages to update their status
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <input
              type="text"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input"
              style={{ width: '250px' }}
            />
            <button
              onClick={() => setFilterDialog(true)}
              className="btn btn-secondary"
            >
              🔍 Filters
              {(filters.showStaleOnly || filters.priority) && (
                <span style={{
                  marginLeft: '0.5rem',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.625rem',
                  background: '#2563EB',
                  color: 'white',
                }}>
                  Active
                </span>
              )}
            </button>
            <button onClick={loadData} className="btn btn-secondary">
              🔄
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>{error}</span>
            <button onClick={loadData} className="btn btn-link">Retry</button>
          </div>
        )}

        {/* Moving indicator */}
        {movingLead && (
          <div style={{
            height: '4px',
            background: 'linear-gradient(90deg, #2563EB, #7C3AED)',
            borderRadius: '2px',
            marginBottom: '1rem',
            animation: 'pulse 1s infinite',
          }} />
        )}

        {/* Bulk Actions */}
        {selectedLeads.length > 0 && (
          <div style={{
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            color: '#1E40AF',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>{selectedLeads.length} lead(s) selected</span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setBulkMoveDialog(true)} className="btn btn-primary btn-sm">
                ➡️ Move ({selectedLeads.length})
              </button>
              <button onClick={() => setSelectedLeads([])} className="btn btn-link">
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Kanban Board */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        overflowX: 'auto',
        paddingBottom: '1rem',
        minHeight: '600px',
      }}>
        {stages.map(stage => (
          <KanbanColumn
            key={stage.id}
            stage={stage}
            leads={filteredLeads}
            onDrop={handleStageDrop}
            onMenuOpen={handleMenuOpen}
            onQuickAction={handleQuickAction}
            selectedLeads={selectedLeads}
            onSelectLead={handleSelectLead}
          />
        ))}
      </div>

      {/* Context Menu */}
      {menuOpen && selectedLead && (
        <>
          <div
            onClick={handleMenuClose}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
          />
          <div style={{
            position: 'fixed',
            top: menuOpen.y,
            left: menuOpen.x,
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid var(--gray-200)',
            zIndex: 1000,
            minWidth: '200px',
          }}>
            <button
              onClick={() => { handleQuickAction('email', selectedLead); handleMenuClose(); }}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              ✉️ Send Email
            </button>
            <button
              onClick={() => { handleQuickAction('call', selectedLead); handleMenuClose(); }}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              📞 Call
            </button>
            <button
              onClick={() => { handleQuickAction('schedule', selectedLead); handleMenuClose(); }}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              📅 Schedule Meeting
            </button>
            <div style={{ borderTop: '1px solid var(--gray-200)', margin: '0.25rem 0' }} />
            <button
              onClick={() => { navigate(`/sales-radar/leads/${selectedLead.id}`); handleMenuClose(); }}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              👤 View Details
            </button>
            <div style={{ borderTop: '1px solid var(--gray-200)', margin: '0.25rem 0' }} />
            {stages.filter(s => s.id !== selectedLead.current_stage).map(stage => (
              <button
                key={stage.id}
                onClick={() => {
                  handleStageDrop(selectedLead.id, stage.id, selectedLead.current_stage);
                  handleMenuClose();
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                ➡️ Move to {stage.name}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Bulk Move Dialog */}
      {bulkMoveDialog && (
        <>
          <div
            onClick={() => setBulkMoveDialog(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            minWidth: '350px',
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--gray-900)' }}>Move Selected Leads</h3>
            <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
              Move {selectedLeads.length} lead(s) to:
            </p>
            {stages.map(stage => (
              <button
                key={stage.id}
                onClick={() => handleBulkMove(stage.id)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  marginBottom: '0.5rem',
                  textAlign: 'left',
                  background: 'white',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = stage.color}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--gray-200)'}
              >
                <span style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: stage.color,
                }} />
                {stage.name}
              </button>
            ))}
            <button
              onClick={() => setBulkMoveDialog(false)}
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Filter Dialog */}
      {filterDialog && (
        <>
          <div
            onClick={() => setFilterDialog(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            minWidth: '350px',
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: 'var(--gray-900)' }}>Filter Leads</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                Score Range: {filters.scoreRange[0]} - {filters.scoreRange[1]}
              </label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.scoreRange[0]}
                  onChange={(e) => setFilters(prev => ({ ...prev, scoreRange: [parseInt(e.target.value) || 0, prev.scoreRange[1]] }))}
                  className="form-input"
                  style={{ width: '80px' }}
                />
                <span style={{ color: 'var(--gray-500)' }}>to</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.scoreRange[1]}
                  onChange={(e) => setFilters(prev => ({ ...prev, scoreRange: [prev.scoreRange[0], parseInt(e.target.value) || 100] }))}
                  className="form-input"
                  style={{ width: '80px' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filters.showStaleOnly}
                  onChange={(e) => setFilters(prev => ({ ...prev, showStaleOnly: e.target.checked }))}
                />
                <span>Show stale leads only (&gt;{STALE_THRESHOLD_DAYS} days inactive)</span>
              </label>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                className="form-select"
                style={{ width: '100%' }}
              >
                <option value="">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setFilters({ scoreRange: [0, 100], showStaleOnly: false, priority: '' })}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Reset
              </button>
              <button
                onClick={() => setFilterDialog(false)}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PipelineKanban;
